import { z } from 'zod';
import { type LlmPurpose, type ModelTier } from './enums';

/**
 * Which models may be routed to, what they cost, and which of them cannot be
 * used at all (docs/DECISIONS.md ZD-046).
 *
 * **This is `framework-policy.ts` one domain along, and for the same reason.**
 * DESIGN_SPEC §13.1 says no client data is used for training and §13.2 says the
 * default provider runs under zero-data-retention terms; `LlmPolicySchema`
 * makes `zero_retention` and `no_training` `z.literal(true)`, so a policy that
 * permits retention is unrepresentable rather than merely discouraged. That
 * settles the tenant's side. It does **not** settle ours: a provider may offer
 * a model that cannot run under ZDR at all, and routing to it would breach a
 * platform-wide restriction while every schema in the system still validated.
 *
 * So model eligibility is data with a refusal behind it, not a sentence in a
 * design document. `zero_retention_available: false` is a **refusal**, and the
 * entry stays in the table rather than being omitted — an omission reads as an
 * oversight and invites a future session to add the model; a refused entry with
 * its reason makes adopting one a decision somebody takes deliberately.
 *
 * **Prices are here so that money can be derived and never stored.** An
 * `llm_calls` row records tokens, which is a fact about what happened; a stored
 * currency figure is arithmetic that goes stale the day a price changes and
 * cannot be recomputed (ZD-035 §3, one column along). `costOfUsd` is the view.
 *
 * Nothing here is a commercial commitment or a quotation. It records the rates
 * a spend estimate is computed from, with the date somebody last checked them.
 */

/**
 * Providers with a policy entry. Closed, so a provider nobody has assessed is
 * unrepresentable in this table.
 *
 * DESIGN_SPEC B.2 treats provider choice as additive — a hosted open-weight
 * endpoint or a self-hosted one is "a new entry in `llm_policy`, not an
 * architecture change" — and this is the entry it means. Adding one is a row
 * here plus an adapter, and both are reviewable.
 *
 * `Tenant.llm_policy.provider` is deliberately **not** narrowed to this enum.
 * A tenant record is configuration and must stay *readable* after a provider is
 * retired, so that "this tenant's configured provider is no longer permitted"
 * is a report rather than a parse failure that hides the tenant entirely. The
 * refusal belongs at selection, where it can name the tenant.
 */
export const PROVIDER_IDS = ['anthropic'] as const;
export const ProviderIdSchema = z.enum(PROVIDER_IDS);
export type ProviderId = z.infer<typeof ProviderIdSchema>;

/**
 * Models with a policy entry. Closed for `FRAMEWORK_IDS`' reason: a model with
 * no entry is unrepresentable rather than merely undiscussed.
 */
export const MODEL_IDS = [
  'claude-opus-5',
  'claude-sonnet-5',
  'claude-haiku-4-5',
  'claude-fable-5',
] as const;
export const ModelIdSchema = z.enum(MODEL_IDS);
export type ModelId = z.infer<typeof ModelIdSchema>;

export interface ModelPolicy {
  provider: ProviderId;
  status: 'permitted' | 'refused';
  /**
   * Which tier this model serves, or `null` where it serves none — a refused
   * model has no tier, which is a different statement from "the cheap tier" and
   * must not be expressible as one.
   */
  tier: ModelTier | null;
  /**
   * Whether the provider will run this model under zero-data-retention terms.
   *
   * `false` is the load-bearing value. §13.1's restriction is platform-wide and
   * not a tenant setting, so a model that cannot honour it is not selectable by
   * anyone, on any tier, at any price.
   */
  zero_retention_available: boolean;
  /**
   * Whether the provider still accepts a sampling `temperature` for this model.
   *
   * **A provider fact, and it invalidates a control this system documents.**
   * DESIGN_SPEC §12.2 says to *"pin `model` and `temperature` (low) per
   * assessment"* and `LlmConfigSnapshotSchema` caps the value at 0.2, on the reasoning
   * that a pinned low temperature is one of the reproducibility anchors. On the
   * current Claude models the parameter is **removed, not merely discouraged**:
   * sending it returns a 400. That is true of every model this table routes a
   * strong-tier purpose to, so the anchor is unavailable to every LLM worker
   * rather than to an unlucky one.
   *
   * Recorded here rather than worked around at the call site, on `status`'s own
   * argument: the terms are data, the refusals are enforced, and a model that
   * will not take a parameter is a fact about the provider in exactly the way
   * `zero_retention_available` is. The router reads it, sends the parameter only
   * where it is accepted, and records **what was actually applied** — a run that
   * logged `0.2` against a model that rejected it would be a false record of the
   * one value the reproducibility story rests on.
   *
   * Found by the first real provider call this repository ever made
   * (`sds-04n`), not by reading the table.
   */
  accepts_temperature: boolean;
  input_usd_per_mtok: number;
  output_usd_per_mtok: number;
  /**
   * Multipliers on the input rate for provider-side prompt caching — a **cost**
   * mechanism, and not the determinism cache (ZD-046 §3). They are different
   * caches with different keys and different purposes, and the sds-08 row's one
   * word "caching" covered both.
   */
  cache_read_multiplier: number;
  cache_write_multiplier: number;
  /**
   * The identifier a run records, or `null` where nobody has established it.
   * `null` means unestablished, never "any version is current".
   */
  current_version: string | null;
  /**
   * The date somebody last confirmed the rates and version above,
   * `YYYY-MM-DD`, or `null` where nobody has.
   *
   * **Without this the prices decay into a lie** — exactly `FrameworkPolicy`'s
   * argument, and sharper here, because a stale rate silently mis-sizes a
   * per-assessment ceiling rather than merely mis-citing a standard.
   */
  confirmed_on: string | null;
  rationale: string;
}

export const MODEL_POLICY: Record<ModelId, ModelPolicy> = {
  'claude-opus-5': {
    provider: 'anthropic',
    status: 'permitted',
    tier: 'strong',
    zero_retention_available: true,
    accepts_temperature: false,
    input_usd_per_mtok: 5,
    output_usd_per_mtok: 25,
    cache_read_multiplier: 0.1,
    cache_write_multiplier: 1.25,
    current_version: 'claude-opus-5',
    confirmed_on: '2026-08-25',
    rationale:
      'The strong tier §13.3 calls for: artefact extraction, contextualisation, QA review and ' +
      'identity judgement. Available under zero-data-retention terms, which is what makes it ' +
      'selectable at all.',
  },
  'claude-sonnet-5': {
    provider: 'anthropic',
    status: 'permitted',
    tier: 'economy',
    zero_retention_available: true,
    accepts_temperature: false,
    input_usd_per_mtok: 3,
    output_usd_per_mtok: 15,
    cache_read_multiplier: 0.1,
    cache_write_multiplier: 1.25,
    current_version: 'claude-sonnet-5',
    confirmed_on: '2026-08-25',
    rationale:
      'The economy tier. **The standard rate is recorded rather than the introductory one** ' +
      'that runs to 2026-08-31: an estimate computed from a price about to rise is one that ' +
      'under-sizes a cost ceiling, and the conservative direction on a ceiling is to over-' +
      'estimate. It also removes a number that would silently become wrong on a date.',
  },
  'claude-haiku-4-5': {
    provider: 'anthropic',
    status: 'permitted',
    tier: null,
    zero_retention_available: true,
    // The one `true` in this table, and the reason the field is data rather
    // than a constant: sampling parameters survive on the pre-4.6 models.
    accepts_temperature: true,
    input_usd_per_mtok: 1,
    output_usd_per_mtok: 5,
    cache_read_multiplier: 0.1,
    cache_write_multiplier: 1.25,
    current_version: 'claude-haiku-4-5',
    confirmed_on: '2026-08-25',
    rationale:
      'Permitted and **routed to by nothing**, which is a declaration rather than an omission ' +
      '(ZD-035 §2). Its 200K context is the smallest in the table and an artefact extraction ' +
      'carries a whole document, so putting it on a tier would be a quality decision taken to ' +
      'save money in a session with no eval to check it. It is here so that a later session ' +
      'with `sds-09` behind it can route to it on evidence.',
  },
  'claude-fable-5': {
    provider: 'anthropic',
    status: 'refused',
    tier: null,
    zero_retention_available: false,
    accepts_temperature: false,
    input_usd_per_mtok: 10,
    output_usd_per_mtok: 50,
    cache_read_multiplier: 0.1,
    cache_write_multiplier: 1.25,
    current_version: 'claude-fable-5',
    confirmed_on: '2026-08-25',
    rationale:
      'Refused, and the refusal is the reason this table exists. It is the most capable model ' +
      'available and it **requires 30-day data retention** — an organisation configured for ' +
      'zero retention gets a 400. DESIGN_SPEC §13.1 makes no-retention platform-wide rather ' +
      'than per tenant, and `LlmPolicySchema` makes `zero_retention: true` unfalsifiable, so ' +
      'the tenant side of that restriction was already airtight while the model side had ' +
      'nothing enforcing it at all. Reopen only if a retention-exempt route exists, which is ' +
      'a contractual question rather than a routing one.',
  },
};

/**
 * Which tier serves which purpose (DESIGN_SPEC §13.3).
 *
 * **§13.3's own tiering was written for a worker that no longer exists**, and
 * the reconciliation is recorded rather than quietly applied. It puts "domain
 * threat workers" on the strong model because, when it was written, a domain
 * worker did the reasoning. ZD-031 moved every applicability decision into code,
 * so what is left for a model is contextualisation — closer in shape to §13.3's
 * own cheap-tier example ("recommendation drafting from templates") than to
 * threat analysis.
 *
 * It stays on `strong` anyway. The prose a contextualiser writes is what a
 * non-specialist approver acts on (ZD-043's entire argument), so moving it down
 * a tier is a **quality** decision, and taking one to save money in a session
 * that cannot measure the quality is the wrong order. The tension is named here
 * so that `sds-09`, which can measure it, inherits a decision to revisit rather
 * than a default nobody noticed.
 *
 * **And the table now returns one value for every input, which is said rather
 * than tidied away** (docs/DECISIONS.md ZD-080 §2). `qa_review` and
 * `recommendation_drafting` came out of `LlmPurpose` — the first because the QA
 * review is deterministic, the second because ZD-079 refused the worker — and
 * `recommendation_drafting` was the only `economy` route. So every remaining
 * purpose is `strong`, and by ZD-058 §4's own test a lookup constant across its
 * population carries no information about its input.
 *
 * It stays a table anyway, and the reason is that ZD-058 §4's finding was about
 * a **client-facing** number claiming to describe a client's system. This claims
 * nothing: it is the one place a tier decision is written down, §13.3's economy
 * tier is a live design position, and `claude-sonnet-5` still carries
 * `tier: 'economy'` and is still selectable as a pin. Collapsing it to a
 * constant would delete the seam that makes the next purpose a one-line
 * decision. What would be dishonest is leaving the constancy unremarked.
 */
export const MODEL_ROUTES: Record<LlmPurpose, ModelTier> = {
  artefact_extraction: 'strong',
  finding_contextualisation: 'strong',
  identity_judgement: 'strong',
};

export class ModelNotSelectableError extends Error {
  constructor(
    readonly model: string,
    readonly why: string,
  ) {
    super(`model ${model} may not be routed to: ${why}`);
    this.name = 'ModelNotSelectableError';
  }
}

export class NoModelForTierError extends Error {
  constructor(
    readonly provider: string,
    readonly tier: ModelTier,
  ) {
    super(`no permitted ${tier}-tier model is configured for provider ${provider}`);
    this.name = 'NoModelForTierError';
  }
}

export function isModelId(value: string): value is ModelId {
  return Object.prototype.hasOwnProperty.call(MODEL_POLICY, value);
}

/**
 * The one place a model is chosen.
 *
 * Fails closed in every direction: an unknown model, a refused one, one whose
 * provider does not match the tenant's, and one no provider offers on the tier
 * the purpose needs. There is no branch that returns a default, because a
 * default here is a model nobody chose being sent a client's architecture.
 */
export function selectModel(
  policy: { provider: string; model: string | null },
  purpose: LlmPurpose,
): ModelId {
  if (policy.model !== null) {
    // A pinned model is the reproducibility anchor (§12.2), so it overrides the
    // tier for every purpose — and is still subject to every refusal below.
    if (!isModelId(policy.model)) {
      throw new ModelNotSelectableError(policy.model, 'no model policy entry exists for it');
    }
    assertSelectable(policy.model, policy.provider);
    return policy.model;
  }
  const tier = MODEL_ROUTES[purpose];
  const candidates = MODEL_IDS.filter((id) => {
    const entry = MODEL_POLICY[id];
    return (
      entry.provider === policy.provider &&
      entry.tier === tier &&
      entry.status === 'permitted' &&
      entry.zero_retention_available
    );
  });
  const chosen = candidates[0];
  if (chosen === undefined) throw new NoModelForTierError(policy.provider, tier);
  return chosen;
}

/**
 * Why this entry may not be routed to, or `null`.
 *
 * **Exported, and taking a `ModelPolicy` rather than a `ModelId`, because one of
 * its branches has no violating input in the shipped table.** The only model
 * that cannot run under zero-data-retention is also `refused`, so the status
 * check fires first and the retention branch below it is unreachable from any
 * real id — a guard nothing can feed a violating input to is a guard nothing
 * checks (ZD-042 §3). Splitting it out lets the test construct the entry that
 * violates it: permitted, on a tier, and not available under ZDR.
 *
 * That is not a redundancy to tidy away. `status` is QRS's editorial call and
 * `zero_retention_available` is a fact about the provider; they coincide today
 * and one does not imply the other. The day a currently-permitted model loses
 * ZDR, this branch is the only thing between it and a client's architecture.
 */
export function selectabilityRefusal(entry: ModelPolicy, provider: string): string | null {
  if (entry.provider !== provider) {
    return `it belongs to provider ${entry.provider}, and this tenant is configured for ${provider}`;
  }
  if (entry.status === 'refused') return entry.rationale;
  if (!entry.zero_retention_available) {
    return (
      'the provider will not run it under zero-data-retention terms, and DESIGN_SPEC §13.1 ' +
      'makes that a platform-wide restriction rather than a tenant setting'
    );
  }
  return null;
}

function assertSelectable(model: ModelId, provider: string): void {
  const why = selectabilityRefusal(MODEL_POLICY[model], provider);
  if (why !== null) throw new ModelNotSelectableError(model, why);
}

/**
 * Token counts, as a call actually reports them. The stored fact.
 *
 * **Zod first and the interface inferred**, because the shape is persisted —
 * `oracle/provider-conformance.json` holds one — and CLAUDE.md makes Zod the
 * single source of truth for a schema. Declaring both by hand would be ZD-017's
 * two copies of one rule, and the copy that drifts is the one nothing parses.
 *
 * `LlmCallRecordSchema` inlines the same four names as flat columns rather than
 * nesting this, and that is not a third copy: a D1 row is a different shape
 * from a nested object, and hoisting one into the other would change the table.
 */
export const TokenUsageSchema = z.strictObject({
  input_tokens: z.int().nonnegative(),
  output_tokens: z.int().nonnegative(),
  cache_read_input_tokens: z.int().nonnegative(),
  cache_write_input_tokens: z.int().nonnegative(),
});
export type TokenUsage = z.infer<typeof TokenUsageSchema>;

/**
 * Money, derived. Never stored, and never the fact a row holds — see this
 * module's header.
 */
export function costOfUsd(model: ModelId, usage: TokenUsage): number {
  const entry = MODEL_POLICY[model];
  const perInput = entry.input_usd_per_mtok / 1_000_000;
  const perOutput = entry.output_usd_per_mtok / 1_000_000;
  return (
    usage.input_tokens * perInput +
    usage.cache_read_input_tokens * perInput * entry.cache_read_multiplier +
    usage.cache_write_input_tokens * perInput * entry.cache_write_multiplier +
    usage.output_tokens * perOutput
  );
}

/**
 * The most a call could cost before it is made.
 *
 * The ceiling is checked **before** the provider is reached, so the estimate has
 * to be an upper bound rather than a guess: a ceiling checked afterwards
 * prevents nothing. Output is charged at `max_output_tokens` because that is the
 * most the call can produce, and input at its full rate because a cache hit can
 * only make it cheaper.
 */
export function maxCostOfUsd(
  model: ModelId,
  estimate: { input_tokens: number; max_output_tokens: number },
): number {
  return costOfUsd(model, {
    input_tokens: estimate.input_tokens,
    output_tokens: estimate.max_output_tokens,
    cache_read_input_tokens: 0,
    cache_write_input_tokens: 0,
  });
}

/**
 * The identity of the rate table itself, bumped whenever a rate moves.
 *
 * **`confirmed_on` cannot do this job, and the reason is what it measures.** It
 * answers *when did somebody last look*, which is a fact about attention rather
 * than about content: it moves on a re-confirmation that changed nothing, and
 * nothing pairs it to a rate edit, so it can equally fail to move when a rate
 * does change. `stalePriceChecks` is right to read it and it is the wrong field
 * to stamp a figure with — in either direction it cannot say which rates
 * produced one.
 *
 * A version can, and only with a guard: `model-policy.test.ts` pins
 * `priceTableFingerprint()`, so editing a rate without bumping this reddens the
 * build in the session that edited it.
 */
export const PRICE_TABLE_VERSION = '1';

/**
 * Every rate this table would price a call at, as one line.
 *
 * **A readable line rather than a hash**, deliberately: the pinned value is
 * compared in a test whose failure message has to say *which rate moved*. A
 * guard that fails with two hex digests reproduces the defect it was written
 * for — a number that changed, with nothing saying what it was about.
 */
export function priceTableFingerprint(): string {
  return MODEL_IDS.map((id) => {
    const entry = MODEL_POLICY[id];
    return (
      `${id} ${String(entry.input_usd_per_mtok)}/${String(entry.output_usd_per_mtok)} ` +
      `x${String(entry.cache_read_multiplier)}/x${String(entry.cache_write_multiplier)}`
    );
  }).join(' | ');
}

/**
 * The rates a figure was computed at, for an artefact that stores one.
 *
 * **Money is derived and never stored — and where a run artefact has no choice
 * but to keep a total, it keeps the rates beside it** (docs/DECISIONS.md
 * ZD-066). `oracle/identity-run.json` is the case: its second pass's tokens are
 * in no cassette, so its `usd` cannot be recomputed and deleting it would
 * destroy the only record of what the run cost. What made that figure drift-
 * prone was never that it was stored — it was that it was stored *without its
 * inputs*, so the day a rate moves nothing can say which table produced it.
 *
 * This is `temperature`'s rule one field along: record what was **applied**,
 * not what is configured now (ZD-050 §15). The artefact already records every
 * other input that determined its contents — `model`, `model_version`,
 * `temperature`, `prompt_version` — and the rates were the one input to `usd`
 * it omitted.
 *
 * **A version pointer alone would not do**, because nothing retains superseded
 * tables: it names an edition nobody can look up. The values are self-pricing,
 * and the version rides along as the handle a human quotes.
 */
export interface AppliedRates {
  price_table_version: string;
  input_usd_per_mtok: number;
  output_usd_per_mtok: number;
  cache_read_multiplier: number;
  cache_write_multiplier: number;
}

export function appliedRates(model: ModelId): AppliedRates {
  const entry = MODEL_POLICY[model];
  return {
    price_table_version: PRICE_TABLE_VERSION,
    input_usd_per_mtok: entry.input_usd_per_mtok,
    output_usd_per_mtok: entry.output_usd_per_mtok,
    cache_read_multiplier: entry.cache_read_multiplier,
    cache_write_multiplier: entry.cache_write_multiplier,
  };
}

/**
 * A cost, priced at the rates a run recorded rather than at today's.
 *
 * The two questions a reader asks of a recorded run are different and only one
 * of them uses the live table: *what did this cost* is priced at the stamp, and
 * *what would a re-record cost* is priced at `costOfUsd`. Answering the first
 * with the second is how a figure silently becomes a forecast.
 */
export function costAtRates(rates: AppliedRates, usage: TokenUsage): number {
  const perInput = rates.input_usd_per_mtok / 1_000_000;
  const perOutput = rates.output_usd_per_mtok / 1_000_000;
  return (
    usage.input_tokens * perInput +
    usage.cache_read_input_tokens * perInput * rates.cache_read_multiplier +
    usage.cache_write_input_tokens * perInput * rates.cache_write_multiplier +
    usage.output_tokens * perOutput
  );
}

/** Beyond this, a confirmation is stale enough that the rates should be re-checked. */
export const PRICE_CHECK_MAX_AGE_DAYS = 120;

export interface StalePriceCheck {
  model: ModelId;
  confirmed_on: string | null;
  days_since: number | null;
}

/**
 * Models whose rates nobody has confirmed lately.
 *
 * `staleVersionChecks`' argument, in the place it bites hardest: a stale price
 * does not merely mis-cite, it mis-sizes every cost ceiling computed from it,
 * and it does so silently and with increasing confidence as time passes.
 */
export function stalePriceChecks(asOf: Date): StalePriceCheck[] {
  const stale: StalePriceCheck[] = [];
  for (const model of MODEL_IDS) {
    const entry = MODEL_POLICY[model];
    if (entry.status !== 'permitted') continue;
    const confirmed = entry.confirmed_on;
    if (confirmed === null) {
      stale.push({ model, confirmed_on: null, days_since: null });
      continue;
    }
    const days = Math.floor((asOf.getTime() - Date.parse(confirmed)) / 86_400_000);
    if (days > PRICE_CHECK_MAX_AGE_DAYS) {
      stale.push({ model, confirmed_on: confirmed, days_since: days });
    }
  }
  return stale;
}
